import { useMutation } from "@apollo/client/react"
import { Bookmark, X } from "lucide-react"
import { useState } from "react"
import type { EditorialSourceFragment } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { SOURCE_DECISION } from "./suggestion-queries"

export function SourceActions({ source }: { source: EditorialSourceFragment }) {
  const [save, { loading }] = useMutation(SOURCE_DECISION)
  const [error, setError] = useState<Error | null>(null)
  const [reason, setReason] = useState(source.decision?.reason ?? "")
  const [feedback, setFeedback] = useState(false)
  const apply = async (status: string) => {
    setError(null)
    try {
      const result = await save({ variables: { input: { sourceId: source.id, status, reason } } })
      if (result.error) throw result.error
      setFeedback(false)
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Could not save source feedback"))
    }
  }
  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={loading}
          onClick={() => void apply(source.decision?.status === "saved" ? "clear" : "saved")}
        >
          <Bookmark className="size-3" />
          {source.decision?.status === "saved" ? "Saved · undo" : "Save for later"}
        </Button>
        <Button size="sm" variant="ghost" disabled={loading} onClick={() => setFeedback(!feedback)}>
          Add note
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={loading}
          onClick={() =>
            source.decision?.status === "dismissed" ? void apply("clear") : setFeedback(!feedback)
          }
        >
          <X className="size-3" />
          {source.decision?.status === "dismissed" ? "Restore" : "Dismiss"}
        </Button>
      </div>
      {feedback && (
        <div className="mt-3 space-y-2">
          <label className="block text-xs" htmlFor={`reason-${source.id}`}>
            A note for your queue (optional)
          </label>
          <input
            id={`reason-${source.id}`}
            className="w-full rounded-md border bg-background p-2 text-sm"
            value={reason}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={loading}
            onClick={() => void apply("saved")}
          >
            Save with note
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={loading}
            onClick={() => void apply("dismissed")}
          >
            Dismiss this source
          </Button>
        </div>
      )}
      {source.decision?.reason && (
        <p className="mt-2 text-xs text-muted-foreground">Your note: {source.decision.reason}</p>
      )}
      {error && <GraphqlError error={error} />}
    </div>
  )
}
